import { Location } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { Component, OnInit } from '@angular/core';
import { Employee } from '@app/models/employee/employee.model';
import { TimesheetEntry } from '@app/models/time-tracking/timesheet-entry.model';
import { TimesheetEntryService, TimesheetOverlapErrorHandlingService } from '@app/modules/time-tracking/services';
import { AuthService, NotifyService } from '@app/services';
import * as Sentry from '@sentry/browser';
import moment from 'moment';

/**
 * MyTimesheetStoreView lets the current user create timesheet entries for themselves
 */
@Component({
    templateUrl: './my-timesheet-store.template.html',
})
export class MyTimesheetStoreView implements OnInit {
    employee: Employee | null = null;
    timesheetEntries: TimesheetEntry[] = [];

    isLoading = true;
    hasLoadedData = false;
    overlapErrors: string[] = [];

    constructor(
        private authService: AuthService,
        private location: Location,
        private notifyService: NotifyService,
        private timesheetEntryBatchService: TimesheetEntryService,
        private timesheetOverlapErrorHandlingService: TimesheetOverlapErrorHandlingService
    ) {}

    async ngOnInit(): Promise<void> {
        this.loadData();
    }

    async onSave(): Promise<void> {
        try {
            this.isLoading = true;
            await this.timesheetEntryBatchService.createBatch(this.timesheetEntries);
            this.notifyService.success('time-tracking.enterHoursPage.success');
            this.location.back();
        } catch (error) {
            const { overlapErrors } = this.timesheetOverlapErrorHandlingService.handleHttpError(
                error as HttpErrorResponse
            );

            this.overlapErrors = overlapErrors;

            Sentry.captureException(error);
            this.isLoading = false;
        }
    }

    /**
     * loadData will load the employee
     * but with their time tracking project assignments.
     *
     * We also create a new timesheet entry to get them started.
     */
    private async loadData(): Promise<void> {
        const authEmployee = this.authService.employee as Employee;
        this.employee = await Employee.param('company', authEmployee.companyId)
            .with('timeTrackingProjectAssignmentsUserCanEnterHoursFor.project')
            .find(authEmployee.id);

        const timesheetEntry = new TimesheetEntry();
        timesheetEntry.employee = this.employee as Employee;
        timesheetEntry.workedOn = moment().format('YYYY-MM-DD');
        this.timesheetEntries = [timesheetEntry];
        this.hasLoadedData = true;
        this.isLoading = false;
    }
}
